import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import JobInputActions from "./shared/JobInputActions";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_SCHEDULED = "scheduled";
const STATUS_DONE = "done";
const STATUS_FAILED = "failed";
const STATUS_CANCELLED = "cancelled";

// Must match template_updater.py's TEMPLATE_MODES.
const MODE_COLUMN = "column";
const MODE_FIXED = "fixed";

const RUN_NOW = "now";
const RUN_SCHEDULE = "schedule";

const ENDPOINT = "/shopify/template-update";

// What the backend renders for a blank suffix, and what it accepts back.
const THEME_DEFAULT_LABEL = "(theme default)";

// Identifier the backend should key on. Values must match the backend's
// IDENTIFIER_COLUMNS in product_tag_adder.py; "" is the auto-cascade.
const IDENTIFIER_OPTIONS = [
  { value: "", label: "Auto (try SKU, then Product ID, then Handle)" },
  { value: "handle", label: "HANDLE — recommended" },
  { value: "sku", label: "SKU / Parent SKU / Variant SKU" },
  { value: "shopify_product_id", label: "SHOPIFY_PRODUCT_ID" },
  { value: "title", label: "Product Title" },
];

const IDENTIFIER_HELPER = {
  "": "Tries SKU, then SHOPIFY_PRODUCT_ID, then HANDLE — whichever columns your file has.",
  handle:
    "Handle is the best key here: the template is per product, and a handle always identifies exactly one product.",
  sku:
    "SKU search is a prefix match, so a short parent SKU can resolve to a different product. Check the report's Matched Title column before running live.",
  shopify_product_id: "Exact match on the Shopify product GID or numeric ID.",
  title: "Exact product title, case-insensitive. Less precise than handle.",
};

// "YYYY-MM-DDTHH:mm" in the user's local time — what <input type="datetime-local"> expects.
const formatLocalDatetimeInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const formatScheduledDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};


function TemplateUpdateTool() {
  const [inputFile, setInputFile] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [templateMode, setTemplateMode] = useState(MODE_COLUMN);
  const [templateSuffix, setTemplateSuffix] = useState("");
  const [identifierColumn, setIdentifierColumn] = useState("handle");
  const [dryRun, setDryRun] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [runTime, setRunTime] = useState(RUN_NOW);
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [downloadingInputJobId, setDownloadingInputJobId] = useState(null);
  const [restartingJobId, setRestartingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const minScheduledAt = formatLocalDatetimeInput(new Date());

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: `${ENDPOINT}/jobs`,
    cacheKey: "templateUpdate.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Template Update",
    description:
      "Set the Shopify theme template on products from an uploaded CSV/XLSX — either one template for every row, or a different one per row. Only the template changes; price, tags, publication and inventory are untouched. Runs as a background job.",
    steps: [
      "Choose where the target template comes from: a column in your file (per row), or one template applied to every row.",
      "Upload a CSV or XLSX and pick which column identifies the product. HANDLE is recommended: the template is per product.",
      "Leave Dry run ticked for the first pass. It resolves every product, reads its current template, and reports exactly what would change without touching Shopify.",
      "Review the report, then re-run with Dry run unticked to apply it.",
      "Optionally schedule it — the file uploads now and the job fires at the time you pick, so a sale-end template restore can land exactly when the sale ends.",
    ],
    templates: [],
  };

  const guideTips = [
    "Why this exists: a sale moves every participating product onto a sale template, and ending the sale has to put each product back on the template it had before — which is usually NOT one shared value.",
    "Per-row mode is the default because it is the revert direction and it cannot blanket-change a catalog. Use one-template-for-all when starting a sale.",
    "A blank cell in the Template Suffix column means the THEME DEFAULT — that is a real target, not a missing value. Many products legitimately sit on the theme default, so a revert file is mostly blank in that column.",
    "Careful: this store also has a literal 'Default product' template, which is NOT the same as the theme default. The tool round-trips both verbatim.",
    "Dry run is the safety net and is ON by default. The template controls what customers see on the product page, so always dry-run a new file first.",
    "The template is per PRODUCT, not per variant. A variant-level export repeats the same handle many times; duplicate rows are reported as 'duplicate_row' rather than applied again.",
    "Rows already on the target template come back as 'no_change' — no Shopify write happens, so re-running is safe.",
    "Scheduled jobs are persisted server-side and survive deploys. Cancel a scheduled job before it runs if plans change; once started it runs to completion.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prev = previous[j.job_id];
      if (prev && prev !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(
            `Template job ${j.job_id} complete. Report is ready.`,
            "success"
          );
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Template job ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        } else if (j.status === STATUS_CANCELLED) {
          addToast(`Template job ${j.job_id} cancelled.`, "info");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload an input file.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    let scheduledIso = null;
    if (runTime === RUN_SCHEDULE) {
      if (!scheduledAt) {
        addToast("Pick a date and time to schedule the job.", "error");
        return;
      }
      const target = new Date(scheduledAt);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast(
          "Scheduled date/time must be at least 30 seconds in the future.",
          "error"
        );
        return;
      }
      scheduledIso = target.toISOString();
    }

    // A live run changes what customers see on the product page, so make it an
    // explicit choice. Setting a blank template for everything is the most
    // destructive variant, so it gets called out by name.
    if (!dryRun) {
      const ok = window.confirm(
        `LIVE RUN — this will change the theme template of the products in ` +
          `"${inputFile.name}" on Shopify.\n\n` +
          (templateMode === MODE_FIXED
            ? `Every matched product will be set to: ${
                templateSuffix.trim() || THEME_DEFAULT_LABEL
              }\n\n`
            : `Each product will be set to its own value from the file's ` +
              `Template Suffix column (blank = theme default).\n\n`) +
          "Continue?"
      );
      if (!ok) return;
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);
    formData.append("template_mode", templateMode);
    // Always sent: in fixed mode an empty string is the legitimate "theme
    // default" value, so the backend keys off the mode, not off emptiness.
    formData.append("template_suffix", templateSuffix.trim());
    formData.append("dry_run", dryRun ? "true" : "false");
    if (identifierColumn) {
      formData.append("identifier_column", identifierColumn);
    }
    if (includeArchived) {
      formData.append("include_archived", "true");
    }
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }
    if (scheduledIso) {
      formData.append("scheduled_at", scheduledIso);
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post(ENDPOINT, formData, {
        onUploadProgress: (evt) => {
          const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
          setUploadProgress(pct);
        },
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      const modeWord = dryRun ? "Dry-run" : "Live";
      if (scheduledIso) {
        addToast(
          `${modeWord} template job ${jobId} scheduled for ${formatScheduledDisplay(
            scheduledIso
          )}.`,
          "info"
        );
      } else {
        addToast(
          `${modeWord} template job ${jobId} queued. Tracking progress…`,
          "info"
        );
      }
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
      setScheduledAt("");
      setRunTime(RUN_NOW);
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to start template job."
      );
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = async (job) => {
    if (!job?.job_id) return;
    const ok = window.confirm(
      `Cancel scheduled template job ${job.job_id}? This can't be undone.`
    );
    if (!ok) return;
    setCancellingJobId(job.job_id);
    try {
      await flaskApi.delete(`${ENDPOINT}/jobs/${job.job_id}`);
      addToast(`Job ${job.job_id} cancelled.`, "success");
      await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to cancel job.");
      addToast(message, "error");
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "template_update_report.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to download report."
      );
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadInput = async (job) => {
    if (!job?.job_id) return;
    setDownloadingInputJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/input`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.input_filename || "input_file"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Input file downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to download input file."
      );
      addToast(message, "error");
    } finally {
      setDownloadingInputJobId(null);
    }
  };

  const handleRestart = async (job, scheduledAtLocal) => {
    if (!job?.job_id) return;
    let scheduledIso = null;
    if (scheduledAtLocal) {
      const target = new Date(scheduledAtLocal);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast(
          "Scheduled date/time must be at least 30 seconds in the future.",
          "error"
        );
        return;
      }
      scheduledIso = target.toISOString();
    }
    setRestartingJobId(job.job_id);
    try {
      await flaskApi.post(
        `${ENDPOINT}/jobs/${job.job_id}/restart`,
        scheduledIso ? { scheduled_at: scheduledIso } : {}
      );
      addToast(
        scheduledIso
          ? `Template job ${job.job_id} rescheduled for ${formatScheduledDisplay(
              scheduledIso
            )}.`
          : `Template job ${job.job_id} restarted.`,
        scheduledIso ? "info" : "success"
      );
      if (page !== 1) await setPage(1);
      else await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to restart job.");
      addToast(message, "error");
    } finally {
      setRestartingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Template Update"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Template Source</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="Template source"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="template-mode"
                      value={MODE_COLUMN}
                      checked={templateMode === MODE_COLUMN}
                      onChange={() => setTemplateMode(MODE_COLUMN)}
                      disabled={uploading}
                    />
                    <span>Per row — from a "Template Suffix" column</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="template-mode"
                      value={MODE_FIXED}
                      checked={templateMode === MODE_FIXED}
                      onChange={() => setTemplateMode(MODE_FIXED)}
                      disabled={uploading}
                    />
                    <span>One template for every row</span>
                  </label>
                  <span className="tool-helper-text tool-radio-hint">
                    {templateMode === MODE_COLUMN
                      ? 'Your file needs a "Template Suffix" column (also accepts "Template" or "Product Template"). A blank cell means the theme default — that is a real value, not a skipped row.'
                      : "Every matched product is set to the same template. Leave the box below empty to put them all on the theme default."}
                  </span>
                </div>
                {templateMode === MODE_FIXED ? (
                  <>
                    <label className="tool-label" htmlFor="template-suffix">
                      Template suffix
                    </label>
                    <input
                      id="template-suffix"
                      type="text"
                      className="tool-input"
                      placeholder="e.g. tent-sale-final-sale (leave blank for theme default)"
                      value={templateSuffix}
                      onChange={(event) => setTemplateSuffix(event.target.value)}
                      disabled={uploading}
                    />
                    <span className="tool-helper-text">
                      The suffix only, not the filename — "tent-sale-final-sale"
                      for product.tent-sale-final-sale.liquid. Note "Default
                      product" is a real template on this store and is NOT the
                      same as leaving this blank.
                    </span>
                  </>
                ) : null}
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input File</h3>
              </div>
              <ToolFileUploadField
                label="Product list"
                file={inputFile}
                setFile={setInputFile}
                accept=".csv,.xlsx,.xls"
                helperText="CSV or XLSX. Needs the column you pick below — HANDLE, SKU / Parent SKU, SHOPIFY_PRODUCT_ID, or Product Title."
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Identify Products By</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="template-identifier">
                  Column
                </label>
                <select
                  id="template-identifier"
                  className="tool-input"
                  value={identifierColumn}
                  onChange={(event) => setIdentifierColumn(event.target.value)}
                  disabled={uploading}
                >
                  {IDENTIFIER_OPTIONS.map((opt) => (
                    <option key={opt.value || "auto"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">
                  {IDENTIFIER_HELPER[identifierColumn]}
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">
                  Tab Name{" "}
                  <span className="tool-section-optional">(optional)</span>
                </h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Excel tab to read</label>
                <p className="tool-helper-text">
                  Only used for .xlsx / .xls files with multiple tabs. Leave blank
                  to use the first tab. Case-sensitive.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Revert - Template"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Safety</h3>
              </div>
              <div className="tool-field-stack">
                <label className="shopify-checkbox-label">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(event) => setDryRun(event.target.checked)}
                    disabled={uploading}
                  />
                  Dry run — report what would change, without touching Shopify
                </label>
                <span className="tool-helper-text">
                  {dryRun
                    ? "Safe. Every product is resolved and its current template read, but no change is made."
                    : "LIVE — changes are applied to Shopify. The template controls what customers see on the product page."}
                </span>
                <label className="shopify-checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(event) =>
                      setIncludeArchived(event.target.checked)
                    }
                    disabled={uploading}
                  />
                  Also match archived products
                </label>
                <span className="tool-helper-text">
                  By default only active products are matched. Draft products are
                  always skipped.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">When to Run</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="When to run"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="template-run-time"
                      value={RUN_NOW}
                      checked={runTime === RUN_NOW}
                      onChange={() => setRunTime(RUN_NOW)}
                      disabled={uploading}
                    />
                    <span>Run now</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="template-run-time"
                      value={RUN_SCHEDULE}
                      checked={runTime === RUN_SCHEDULE}
                      onChange={() => setRunTime(RUN_SCHEDULE)}
                      disabled={uploading}
                    />
                    <span>Schedule for later</span>
                  </label>
                </div>
                {runTime === RUN_SCHEDULE ? (
                  <>
                    <label className="tool-label" htmlFor="template-scheduled-at">
                      Date &amp; time
                    </label>
                    <input
                      id="template-scheduled-at"
                      type="datetime-local"
                      className="tool-input"
                      value={scheduledAt}
                      min={minScheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                      disabled={uploading}
                      required
                    />
                    <span className="tool-helper-text">
                      Uses your browser's local time. The file uploads now and the
                      job fires at the scheduled time — handy for landing a
                      template restore exactly when a sale ends.
                    </span>
                  </>
                ) : null}
              </div>
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={uploading}
              >
                {uploading
                  ? "Uploading…"
                  : `${runTime === RUN_SCHEDULE ? "Schedule" : "Start"} ${
                      dryRun ? "Dry Run" : "Live Template Update"
                    }`}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Template Jobs"
          emptyText="No template jobs yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
            const progress = j.progress || {};
            const total = Number(progress.total) || 0;
            const processed = Number(progress.processed) || 0;
            const pct =
              total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
            // dry_run may be absent on an in-flight doc; progress carries it too.
            const jobDryRun =
              j.dry_run !== undefined ? j.dry_run : progress.dry_run;
            const jobMode = j.template_mode || progress.template_mode || "";
            const jobTarget =
              jobMode === MODE_FIXED
                ? j.template_suffix || THEME_DEFAULT_LABEL
                : "per row";
            return (
              <div key={j.job_id} className="tool-job-status">
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Job ID</span>
                  <span className="tool-job-value">{j.job_id}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Template</span>
                  <span className="tool-job-value">
                    {jobTarget}
                    {jobDryRun === false ? " (LIVE)" : jobDryRun ? " (dry run)" : ""}
                  </span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Input</span>
                  <span className="tool-job-value">{j.input_filename || "—"}</span>
                </div>
                {j.sheet_name ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Tab</span>
                    <span className="tool-job-value">{j.sheet_name}</span>
                  </div>
                ) : null}
                {j.scheduled_at ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Scheduled</span>
                    <span className="tool-job-value">
                      {formatScheduledDisplay(j.scheduled_at)}
                    </span>
                  </div>
                ) : null}
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Status</span>
                  <span className={`tool-job-value tool-job-status-${j.status}`}>
                    {j.status}
                  </span>
                </div>
                {total > 0 ? (
                  <>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Progress</span>
                      <span className="tool-job-value">
                        {processed.toLocaleString()} / {total.toLocaleString()} (
                        {pct}%)
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </>
                ) : null}
                <div className="tool-job-counts">
                  <span>Updated: {progress.updated || 0}</span>
                  <span>Would change: {progress.planned || 0}</span>
                  <span>No change: {progress.no_change || 0}</span>
                  <span>Duplicates: {progress.duplicates || 0}</span>
                  <span>Not found: {progress.not_found || 0}</span>
                  <span>Errors: {progress.errors || 0}</span>
                  <span>Skipped: {progress.skipped || 0}</span>
                </div>
                {j.status === STATUS_FAILED && j.error ? (
                  <div className="tool-job-error">Error: {j.error}</div>
                ) : null}
                {j.status === STATUS_DONE ? (
                  <button
                    type="button"
                    className="tool-btn-primary"
                    onClick={() => handleDownload(j)}
                    disabled={downloadingJobId === j.job_id}
                  >
                    {downloadingJobId === j.job_id
                      ? "Downloading…"
                      : "Download Report"}
                  </button>
                ) : null}
                {j.status === STATUS_SCHEDULED ? (
                  <button
                    type="button"
                    className="tool-btn-secondary"
                    onClick={() => handleCancel(j)}
                    disabled={cancellingJobId === j.job_id}
                  >
                    {cancellingJobId === j.job_id ? "Cancelling…" : "Cancel"}
                  </button>
                ) : null}
                <JobInputActions
                  job={j}
                  onDownloadInput={handleDownloadInput}
                  downloadingInput={downloadingInputJobId === j.job_id}
                  onRestart={handleRestart}
                  restarting={restartingJobId === j.job_id}
                  minScheduledAt={minScheduledAt}
                />
              </div>
            );
          }}
        />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default TemplateUpdateTool;
